import React, { useState, useEffect } from "react";
import { DatePicker, Select, Input, Button } from "antd";
import { useNavigate } from "react-router-dom";
import { getPharmacyItems } from "../../apis/pharmacyItems";
import { createGRN, getGRNById, updateGRN } from "../../apis/grn";
import { getSuppliers } from "../../apis/Supplier";
import { GRNItem } from "../../interfaces/Grn";
import { getPurchaseOrders } from "../../apis/purchaseorder";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";

const { Option } = Select;
const today = dayjs();

type AddGRNForm = {
  closeModal?: () => void;
};

const AddGRNForm: React.FC<AddGRNForm> = ({ closeModal }) => {
  const [supplierId, setSupplierId] = useState<string>();
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [purchaseOrder, setPurchaseOrder] = useState<string>();
  const [invoiceNumber, setInvoiceNumber] = useState<string>("");
  const [receivedDate, setReceivedDate] = useState<any>(today);
  const [invoiceDate, setInvoiceDate] = useState<any>(today);
  const [items, setItems] = useState<GRNItem[]>([]);
  const [pharmacyItems, setPharmacyItems] = useState<any[]>([]);
  const [purchaseOrders, setPurchaseOrders] = useState<any[]>([]);
  const [isLoadingItems, setIsLoadingItems] = useState<boolean>(false);
  const [itemSelectValue, setItemSelectValue] = useState<string | any>();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  useEffect(() => {
    if (id) {
      getGRNById(Number(id))
        .then((res: any) => {
          const grn = res.data;
          console.log("GRN", grn);
          setSupplierId(grn.supplierId.toString());
          setPurchaseOrder(grn.purchaseOrderId?.toString());
          setReceivedDate(grn.receivedDate ? dayjs(grn.receivedDate) : null);
          setInvoiceNumber(grn.invoiceNumber);
          setInvoiceDate(grn.invoiceDate ? dayjs(grn.invoiceDate) : null);

          setItems(
            grn.items.map((item: any) => ({
              id: String(item.productId ?? item.id),
              name: item.productName ?? item.name ?? item.pharmacyItem?.name ?? "",
              receivedQuantity: item.receivedQuantity,
              freeQuantity: item.freeQuantity,
              purchasePrice: item.purchasePrice,
              sellingPrice: item.sellingPrice,
              mrp: item.mrp,
              discount: item.discountPercentage,
              tax: item.taxPercentage,
              batchNumber: item.batchNumber,
              mfgDate: item.manufacturingDate
                ? dayjs(item.manufacturingDate)
                : null,
              expiryDate: item.expiryDate ? dayjs(item.expiryDate) : null,
              landingCost: item.landingCost ?? 0,
            })),
          );
        })
        .catch((err) => console.error(err));
    }
  }, [id]);

  // Load suppliers, pharmacy items & purchase orders
  useEffect(() => {
    getSuppliers()
      .then((res: any) => setSuppliers(res.data || []))
      .catch(console.error);

    fetchPharmacyItems();

    getPurchaseOrders()
      .then((res: any) => setPurchaseOrders(res.data || []))
      .catch(console.error);
  }, []);

  const fetchPharmacyItems = async (search: string = "") => {
    setIsLoadingItems(true);
    try {
      const params: any = { autocomplete: true };
      if (search && search.trim()) {
        params.search = search.trim();
      }
      // const itemsRes = await getPharmacyItems(params);
      // setPharmacyItems(Array.isArray(itemsRes) ? itemsRes : []);
      const res: any = await getPharmacyItems(params);
      console.log("Pharmacy Items API Response", res);

      setPharmacyItems(res.data || []);
    } catch (err) {
      setPharmacyItems([]);
    } finally {
      setIsLoadingItems(false);
    }
  };

  const handleItemSearch = (value: string) => {
    fetchPharmacyItems(value);
  };

  const handleAddItem = (itemId: string) => {
    const item = pharmacyItems.find((i) => String(i.id) === String(itemId));
    if (item && !items.some((i) => String(i.id) === String(itemId))) {
      setItems([
        ...items,
        {
          id: String(itemId),
          name: item.name ?? item.pharmacyItem?.name ?? "",
          receivedQuantity: 0,
          freeQuantity: 0,
          purchasePrice: 0,
          sellingPrice: 0,
          mrp: 0,
          discount: 0,
          tax: 0,
          invoiceNumber: 0,
          landingCost: 0,
          batchNumber: "",
          mfgDate: null,
          expiryDate: null,
        },
      ]);
      setItemSelectValue(undefined);
    }
  };

  const navigate = useNavigate();
  const updateItem = (index: number, field: string, value: any) => {
    const newItems = [...items];
    (newItems[index] as any)[field] = value;

    // Auto-calculate landing cost
    const price = newItems[index].purchasePrice;
    const discount = newItems[index].discount;
    const tax = newItems[index].tax;

    const discountedPrice = price - (price * discount) / 100;

    const quantity =
      newItems[index].receivedQuantity + newItems[index].freeQuantity;
    const totalAmount = discountedPrice * quantity;
    const taxAmount = (totalAmount * tax) / 100;
    const landingCost = totalAmount + taxAmount;
    newItems[index].landingCost = parseFloat(landingCost.toFixed(2));

    setItems(newItems);
  };

  const handleSubmit = async () => {
    const payload = {
      supplierId,
      purchaseOrderId: purchaseOrder,
      receivedDate: receivedDate ? receivedDate.format("YYYY-MM-DD") : null,
      invoiceNumber,
      invoiceDate: invoiceDate ? invoiceDate.format("YYYY-MM-DD") : null,
      items: items.map((item) => ({
        product_id: item.id,
        received_quantity: item.receivedQuantity,
        free_quantity: item.freeQuantity,
        purchase_price: item.purchasePrice,
        selling_price: item.sellingPrice,
        discount_percentage: item.discount,
        tax_percentage: item.tax,
        invoice_number: invoiceNumber,
        mrp: item.mrp,
        batch_number: item.batchNumber,
        landing_cost: item.landingCost,
        manufacturing_date: item.mfgDate
          ? item.mfgDate.format("YYYY-MM-DD")
          : null,
        expiry_date: item.expiryDate
          ? item.expiryDate.format("YYYY-MM-DD")
          : null,
      })),
    };

    try {
      if (id) {
        // UPDATE
        await updateGRN(Number(id), payload as any);
      } else {
        // CREATE
        await createGRN(payload as any);
      }
      navigate("/grns");
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="p-4 lg:p-6">
      <h1 className="text-lg font-semibold text-gray-900">
        Good Received Notes
      </h1>
      <p className="mt-1 text-sm text-gray-600">
        {id
          ? "Edit the form to update Good Received Notes."
          : "Fill the form to create Good Received Notes."}
      </p>

      <div className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-3">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900">
              Supplier
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Select Supplier"
              value={supplierId}
              onChange={(val) => setSupplierId(val.toString())}
              className="w-full"
              size="middle"
            >
              {suppliers.map((s) => (
                <Option key={s.id} value={s.id.toString()}>
                  {s.name}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900">
              Purchase Order
            </label>
            <Select
              showSearch
              allowClear
              placeholder="Select Purchase Order"
              value={purchaseOrder}
              onChange={(val) => setPurchaseOrder(val.toString())}
              className="w-full"
              size="middle"
            >
              {purchaseOrders.map((po) => (
                <Option key={po.id} value={po.id.toString()}>
                  {po.poNumber}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900">
              Received Date
            </label>
            <DatePicker
              value={receivedDate}
              onChange={setReceivedDate}
              className="w-full"
              size="middle"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 mb-4 sm:grid-cols-3">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900">
              Invoice Number
            </label>
            <Input
              value={invoiceNumber}
              onChange={(e) => setInvoiceNumber(e.target.value)}
              placeholder="Enter invoice number"
              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              size="middle"
            />
          </div>

          <div>
            <label className="block mb-1 text-sm font-medium text-gray-900">
              Invoice Date
            </label>
            <DatePicker
              value={invoiceDate}
              onChange={setInvoiceDate}
              className="w-full"
              size="middle"
            />
          </div>
        </div>

        <div className="pt-5 border-t border-gray-200">
          <div className="mb-4">
            <h3 className="mb-3 text-sm font-semibold text-gray-900">Items</h3>
            <Select
              showSearch
              allowClear
              placeholder="Search and select item to add"
              value={itemSelectValue}
              onChange={(val) => {
                if (val) {
                  handleAddItem(val as string);
                  setTimeout(() => {
                    setItemSelectValue(null);
                  }, 100);
                } else {
                  setItemSelectValue(null);
                }
              }}
              onSearch={handleItemSearch}
              filterOption={false}
              loading={isLoadingItems}
              size="middle"
              className="w-full max-w-md"
              notFoundContent={isLoadingItems ? "Loading..." : "No items found"}
            >
              {pharmacyItems
                .filter(
                  (item) =>
                    !items.some(
                      (selected) =>
                        String(selected.id) === String(item.id),
                    ),
                )
                .map((item) => (
                  <Option key={item.id} value={String(item.id)}>
                    <div className="flex flex-col">
                      <span className="font-medium">{item.name}</span>
                    </div>
                  </Option>
                ))}
            </Select>
          </div>

          {items.length === 0 ? (
            <div className="py-12 text-center text-gray-500 bg-gray-50 rounded-lg border-2 border-gray-300 border-dashed">
              <p className="text-sm">
                No items added yet. Search and select an item to add.
              </p>
            </div>
          ) : (
            <>
              {JSON.stringify(items)}

              {/* {JSON.stringify(pharmacyItems)} */}
              <div className="space-y-4">
                {items.map((item, idx) => {
                  const selectedItem = pharmacyItems.find(
                    (p) => String(p.id) === String(item.id),
                  );
                  { JSON.stringify(selectedItem) }
                  return (
                    <div
                      key={idx}
                      className="bg-white rounded-lg border border-gray-200 shadow-sm transition-shadow hover:shadow-md"
                    >
                      <div className="p-4">
                        <div className="flex justify-between items-start pb-2 mb-3 border-b border-gray-200">
                          <div className="flex-1">
                            {selectedItem || item.name ? (
                              <div>
                                <div className="font-semibold text-gray-900 capitalize">
                                  {selectedItem?.name ?? selectedItem?.pharmacyItem?.name ?? item.name}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 capitalize">
                                  {selectedItem?.form ?? ""}
                                </div>
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">
                                Item not found
                              </span>
                            )}
                          </div>
                          <Button
                            type="text"
                            danger
                            size="small"
                            onClick={() => {
                              const newItems = items.filter((_, i) => i !== idx);
                              setItems(newItems);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            Remove
                          </Button>
                        </div>

                        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Received Quantity
                            </label>
                            <Input
                              type="number"
                              placeholder="Enter quantity"
                              value={item.receivedQuantity}
                              onChange={(e) =>
                                updateItem(
                                  idx,
                                  "receivedQuantity",
                                  Number(e.target.value),
                                )
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Free Quantity
                            </label>
                            <Input
                              type="number"
                              placeholder="Enter free quantity"
                              value={item.freeQuantity}
                              onChange={(e) =>
                                updateItem(
                                  idx,
                                  "freeQuantity",
                                  Number(e.target.value),
                                )
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Purchase Price
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.purchasePrice}
                              onChange={(e) =>
                                updateItem(
                                  idx,
                                  "purchasePrice",
                                  Number(e.target.value),
                                )
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              MRP
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.mrp}
                              onChange={(e) =>
                                updateItem(idx, "mrp", Number(e.target.value))
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Discount (%)
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.discount}
                              onChange={(e) =>
                                updateItem(
                                  idx,
                                  "discount",
                                  Number(e.target.value),
                                )
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Selling Price
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.sellingPrice || 0}
                              onChange={(e) =>
                                updateItem(
                                  idx,
                                  "sellingPrice",
                                  Number(e.target.value),
                                )
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Tax (%)
                            </label>
                            <Select
                              value={item.tax}
                              onChange={(val) =>
                                updateItem(idx, "tax", Number(val))
                              }
                              className="w-full"
                              size="middle"
                            >
                              <Option value={0}>0%</Option>
                              <Option value={5}>5%</Option>
                              <Option value={12}>12%</Option>
                              <Option value={18}>18%</Option>
                              <Option value={28}>28%</Option>
                            </Select>
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Landing Cost
                            </label>
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="0.00"
                              value={item.landingCost}
                              onChange={(e) =>
                                updateItem(
                                  idx,
                                  "landingCost",
                                  Number(e.target.value),
                                )
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                              disabled
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Batch Number
                            </label>
                            <Input
                              placeholder="Enter batch number"
                              value={item.batchNumber}
                              onChange={(e) =>
                                updateItem(idx, "batchNumber", e.target.value)
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Manufacturing Date
                            </label>
                            <DatePicker
                              placeholder="Select MFG date"
                              value={item.mfgDate}
                              disabledDate={(current) =>
                                current && current > dayjs().endOf("day")
                              }
                              onChange={(date) =>
                                updateItem(idx, "mfgDate", date)
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>

                          <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1.5">
                              Expiry Date
                            </label>
                            <DatePicker
                              placeholder="Select expiry date"
                              value={item.expiryDate}
                              disabledDate={(current) => {
                                if (!current) return false;

                                const isBeforeToday =
                                  current < dayjs().startOf("day");
                                const isBeforeMfg =
                                  item.mfgDate &&
                                  current <= dayjs(item.mfgDate).startOf("day");

                                return isBeforeToday || isBeforeMfg;
                              }}
                              onChange={(date) =>
                                updateItem(idx, "expiryDate", date)
                              }
                              className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                              size="middle"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

        </div>

        <div className="flex gap-3 justify-end items-center pt-4 mt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white rounded-md border border-gray-300 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="px-4 py-2 text-sm font-medium text-white rounded-md shadow-sm bg-primary hover:bg-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div >
  );
};

export default AddGRNForm;
